/**
 * X-Road Control Tower - Zustand Store
 *
 * Manages UI state for the X-Road module:
 * - Active tab/page
 * - Selected items
 * - Filters
 * - Modal states
 */

import { create } from 'zustand';
import { persist } from 'zustand/middleware';
import type { ApiKeyEnvironment, ApiKeyStatus, ServerConnectionStatus } from '../types/xroad.types';
import type { TenantType, HostingTier, TenantMetadata } from '../types/tenant.types';
import { TenantType as TenantTypeEnum } from '../types/tenant.types';

// ============================================================================
// State Interface
// ============================================================================

interface XRoadState {
  // ===== Tenant Type (Multi-Mode Support) =====
  tenantType: TenantType;
  hostingTier: HostingTier | null;
  maxServices: number | null;
  maxApiCallsPerMonth: number | null;
  tenantMetadata: TenantMetadata | null;

  // ===== Navigation =====
  activeTab: 'dashboard' | 'servers' | 'api-keys' | 'services' | 'analytics';

  // ===== Selected Items =====
  selectedServerId: number | null;
  selectedApiKeyId: number | null;
  selectedServiceId: number | null;

  // ===== Filters =====
  serverFilter: {
    search: string;
    connectionStatus?: ServerConnectionStatus;
    isPrimary?: boolean;
  };

  apiKeyFilter: {
    search: string;
    environment?: ApiKeyEnvironment;
    status?: ApiKeyStatus;
    xroadMemberCode?: string;
    expiringWithinDays?: number;
  };

  serviceFilter: {
    search: string;
    endpointId?: number;
  };

  // ===== Modal States =====
  isAddServerModalOpen: boolean;
  isEditServerModalOpen: boolean;
  isCreateApiKeyModalOpen: boolean;
  isEditApiKeyModalOpen: boolean;
  isRevokeApiKeyModalOpen: boolean;
  isApiKeyDetailsModalOpen: boolean;
  isPublishServiceModalOpen: boolean;

  // ===== Actions - Tenant Type =====
  setTenantType: (type: TenantType) => void;
  setHostingTier: (tier: HostingTier | null) => void;
  setTenantMetadata: (metadata: TenantMetadata | null) => void;

  // ===== Actions - Navigation =====
  setActiveTab: (tab: XRoadState['activeTab']) => void;

  // ===== Actions - Selection =====
  setSelectedServerId: (id: number | null) => void;
  setSelectedApiKeyId: (id: number | null) => void;
  setSelectedServiceId: (id: number | null) => void;

  // ===== Actions - Filters =====
  setServerFilter: (filter: Partial<XRoadState['serverFilter']>) => void;
  resetServerFilter: () => void;

  setApiKeyFilter: (filter: Partial<XRoadState['apiKeyFilter']>) => void;
  resetApiKeyFilter: () => void;

  setServiceFilter: (filter: Partial<XRoadState['serviceFilter']>) => void;
  resetServiceFilter: () => void;

  // ===== Actions - Modals =====
  openAddServerModal: () => void;
  closeAddServerModal: () => void;

  openEditServerModal: (serverId: number) => void;
  closeEditServerModal: () => void;

  openCreateApiKeyModal: () => void;
  closeCreateApiKeyModal: () => void;

  openEditApiKeyModal: (apiKeyId: number) => void;
  closeEditApiKeyModal: () => void;

  openRevokeApiKeyModal: (apiKeyId: number) => void;
  closeRevokeApiKeyModal: () => void;

  openApiKeyDetailsModal: (apiKeyId: number) => void;
  closeApiKeyDetailsModal: () => void;

  openPublishServiceModal: () => void;
  closePublishServiceModal: () => void;

  // ===== Actions - Utility =====
  resetAllFilters: () => void;
  closeAllModals: () => void;
}

// ============================================================================
// Initial State
// ============================================================================

const initialServerFilter = {
  search: '',
  connectionStatus: undefined,
  isPrimary: undefined,
};

const initialApiKeyFilter = {
  search: '',
  environment: undefined,
  status: undefined,
  xroadMemberCode: undefined,
  expiringWithinDays: undefined,
};

const initialServiceFilter = {
  search: '',
  endpointId: undefined,
};

// ============================================================================
// Store Implementation
// ============================================================================

export const useXRoadStore = create<XRoadState>()(
  persist(
    (set) => ({
      // ===== Initial State - Tenant Type (Mock Data for Development) =====
      // Default to CENTRAL_AUTHORITY for testing SENUM console
      // Change via setMockTenantType() in browser console
      tenantType: TenantTypeEnum.CENTRAL_AUTHORITY,
      hostingTier: null, // Central Authority doesn't have a hosting tier
      maxServices: null, // Unlimited for SENUM
      maxApiCallsPerMonth: null, // Unlimited for SENUM
      tenantMetadata: {
        tenantId: 'senum',
        tenantName: 'SENUM - Sénégal Numérique',
        tenantType: TenantTypeEnum.CENTRAL_AUTHORITY,
        parentTenantId: null,
        hostingTier: null,
        maxServices: null,
        maxApiCallsPerMonth: null,
        xroadMemberIdentifier: null,
        isActive: true,
      },

      // ===== Initial State - Navigation =====
      activeTab: 'dashboard',

      selectedServerId: null,
      selectedApiKeyId: null,
      selectedServiceId: null,

      serverFilter: initialServerFilter,
      apiKeyFilter: initialApiKeyFilter,
      serviceFilter: initialServiceFilter,

      isAddServerModalOpen: false,
      isEditServerModalOpen: false,
      isCreateApiKeyModalOpen: false,
      isEditApiKeyModalOpen: false,
      isRevokeApiKeyModalOpen: false,
      isApiKeyDetailsModalOpen: false,
      isPublishServiceModalOpen: false,

      // ===== Actions - Tenant Type =====
      setTenantType: (type) => set({ tenantType: type }),
      setHostingTier: (tier) => set({ hostingTier: tier }),
      setTenantMetadata: (metadata) => {
        set({
          tenantMetadata: metadata,
          tenantType: metadata?.tenantType || TenantTypeEnum.CENTRAL_AUTHORITY,
          hostingTier: metadata?.hostingTier || null,
          maxServices: metadata?.maxServices || null,
          maxApiCallsPerMonth: metadata?.maxApiCallsPerMonth || null,
        });
      },

      // ===== Actions - Navigation =====
      setActiveTab: (tab) => set({ activeTab: tab }),

      // ===== Actions - Selection =====
      setSelectedServerId: (id) => set({ selectedServerId: id }),
      setSelectedApiKeyId: (id) => set({ selectedApiKeyId: id }),
      setSelectedServiceId: (id) => set({ selectedServiceId: id }),

      // ===== Actions - Filters =====
      setServerFilter: (filter) =>
        set((state) => ({
          serverFilter: { ...state.serverFilter, ...filter },
        })),

      resetServerFilter: () => set({ serverFilter: initialServerFilter }),

      setApiKeyFilter: (filter) =>
        set((state) => ({
          apiKeyFilter: { ...state.apiKeyFilter, ...filter },
        })),

      resetApiKeyFilter: () => set({ apiKeyFilter: initialApiKeyFilter }),

      setServiceFilter: (filter) =>
        set((state) => ({
          serviceFilter: { ...state.serviceFilter, ...filter },
        })),

      resetServiceFilter: () => set({ serviceFilter: initialServiceFilter }),

      // ===== Actions - Modals - Security Server =====
      openAddServerModal: () => set({ isAddServerModalOpen: true }),
      closeAddServerModal: () => set({ isAddServerModalOpen: false }),

      openEditServerModal: (serverId) =>
        set({
          selectedServerId: serverId,
          isEditServerModalOpen: true,
        }),
      closeEditServerModal: () =>
        set({
          isEditServerModalOpen: false,
          selectedServerId: null,
        }),

      // ===== Actions - Modals - API Keys =====
      openCreateApiKeyModal: () => set({ isCreateApiKeyModalOpen: true }),
      closeCreateApiKeyModal: () => set({ isCreateApiKeyModalOpen: false }),

      openEditApiKeyModal: (apiKeyId) =>
        set({
          selectedApiKeyId: apiKeyId,
          isEditApiKeyModalOpen: true,
        }),
      closeEditApiKeyModal: () =>
        set({
          isEditApiKeyModalOpen: false,
          selectedApiKeyId: null,
        }),

      openRevokeApiKeyModal: (apiKeyId) =>
        set({
          selectedApiKeyId: apiKeyId,
          isRevokeApiKeyModalOpen: true,
        }),
      closeRevokeApiKeyModal: () =>
        set({
          isRevokeApiKeyModalOpen: false,
          selectedApiKeyId: null,
        }),

      openApiKeyDetailsModal: (apiKeyId) =>
        set({
          selectedApiKeyId: apiKeyId,
          isApiKeyDetailsModalOpen: true,
        }),
      closeApiKeyDetailsModal: () =>
        set({
          isApiKeyDetailsModalOpen: false,
          selectedApiKeyId: null,
        }),

      // ===== Actions - Modals - Services =====
      openPublishServiceModal: () => set({ isPublishServiceModalOpen: true }),
      closePublishServiceModal: () => set({ isPublishServiceModalOpen: false }),

      // ===== Actions - Utility =====
      resetAllFilters: () =>
        set({
          serverFilter: initialServerFilter,
          apiKeyFilter: initialApiKeyFilter,
          serviceFilter: initialServiceFilter,
        }),

      closeAllModals: () =>
        set({
          isAddServerModalOpen: false,
          isEditServerModalOpen: false,
          isCreateApiKeyModalOpen: false,
          isEditApiKeyModalOpen: false,
          isRevokeApiKeyModalOpen: false,
          isApiKeyDetailsModalOpen: false,
          isPublishServiceModalOpen: false,
          selectedServerId: null,
          selectedApiKeyId: null,
          selectedServiceId: null,
        }),
    }),
    {
      name: 'xroad-storage', // localStorage key
      partialize: (state) => ({
        // Only persist these fields
        tenantType: state.tenantType,
        hostingTier: state.hostingTier,
        maxServices: state.maxServices,
        maxApiCallsPerMonth: state.maxApiCallsPerMonth,
        tenantMetadata: state.tenantMetadata,
        activeTab: state.activeTab,
        serverFilter: state.serverFilter,
        apiKeyFilter: state.apiKeyFilter,
        serviceFilter: state.serviceFilter,
      }),
    }
  )
);

// ============================================================================
// Selector Hooks (for better performance and reusability)
// ============================================================================

// Tenant Type Selectors
export const useTenantType = () => useXRoadStore((state) => state.tenantType);
export const useHostingTier = () => useXRoadStore((state) => state.hostingTier);
export const useTenantMetadata = () => useXRoadStore((state) => state.tenantMetadata);
export const useMaxServices = () => useXRoadStore((state) => state.maxServices);
export const useMaxApiCallsPerMonth = () => useXRoadStore((state) => state.maxApiCallsPerMonth);

// Navigation Selectors
export const useActiveTab = () => useXRoadStore((state) => state.activeTab);
export const useSelectedServerId = () => useXRoadStore((state) => state.selectedServerId);
export const useSelectedApiKeyId = () => useXRoadStore((state) => state.selectedApiKeyId);
export const useServerFilter = () => useXRoadStore((state) => state.serverFilter);
export const useApiKeyFilter = () => useXRoadStore((state) => state.apiKeyFilter);
